import { useSearchParams } from 'react-router-dom'

type FieldCodec<T> = {
    default: T
    parse: (raw: string) => T
    serialize: (val: T) => string | null
    urlKey?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UrlCodec = Record<string, FieldCodec<any>>

type ExtractFieldType<F> = F extends FieldCodec<infer T> ? T : never

export type StateFromCodec<C extends UrlCodec> = {
    [K in keyof C]: ExtractFieldType<C[K]>
}

type FieldOpts = { urlKey?: string }
type StringArrayOpts = FieldOpts & { defaultValue?: string[] }
type IntOpts = FieldOpts & { defaultValue?: number }

export const codec = {
    string: (opts?: FieldOpts): FieldCodec<string | undefined> => ({
        default: undefined,
        parse: (raw) => (raw ? decodeURIComponent(raw) : undefined),
        serialize: (val) => (val ? encodeURIComponent(val) : null),
        urlKey: opts?.urlKey,
    }),

    stringArray: (opts?: StringArrayOpts): FieldCodec<string[]> => {
        const defaultValue = opts?.defaultValue ?? []
        const isDefault = (val: string[]) =>
            val.length === defaultValue.length &&
            val.every((v) => defaultValue.includes(v))
        return {
            default: defaultValue,
            parse: (raw) => (raw ? raw.split(',').map(decodeURIComponent) : []),
            serialize: (val) =>
                val.length > 0 && !isDefault(val)
                    ? val.map(encodeURIComponent).join(',')
                    : null,
            urlKey: opts?.urlKey,
        }
    },

    float: (opts?: FieldOpts): FieldCodec<number | undefined> => ({
        default: undefined,
        parse: (raw) => {
            const n = parseFloat(raw)
            return isNaN(n) ? undefined : n
        },
        serialize: (val) => (val !== undefined ? String(val) : null),
        urlKey: opts?.urlKey,
    }),

    int: (opts?: IntOpts): FieldCodec<number | undefined> => {
        const defaultValue = opts?.defaultValue
        return {
            default: defaultValue,
            parse: (raw) => {
                const n = parseInt(raw, 10)
                return isNaN(n) ? undefined : n
            },
            serialize: (val) =>
                val !== undefined && val !== defaultValue ? String(val) : null,
            urlKey: opts?.urlKey,
        }
    },

    boolean: (opts?: FieldOpts): FieldCodec<boolean> => ({
        default: false,
        parse: (raw) => raw === 'true',
        serialize: (val) => (val ? 'true' : null),
        urlKey: opts?.urlKey,
    }),
}

type UseUrlStateOpts = {
    replace?: boolean
}

export function useUrlState<C extends UrlCodec>(
    codec: C,
    opts?: UseUrlStateOpts
) {
    const [params, setParams] = useSearchParams()
    const replace = opts?.replace ?? true

    const state = {} as StateFromCodec<C>
    for (const stateKey in codec) {
        const field = codec[stateKey]
        const urlKey = field.urlKey ?? stateKey
        const raw = params.get(urlKey)
        ;(state as Record<string, unknown>)[stateKey] =
            raw !== null ? field.parse(raw) : field.default
    }

    const setState = (patch: Partial<StateFromCodec<C>>) => {
        const merged = { ...state, ...patch }
        setParams(
            () => {
                const next = new URLSearchParams(params)
                for (const stateKey in codec) {
                    const field = codec[stateKey]
                    const urlKey = field.urlKey ?? stateKey
                    const serialized = field.serialize(
                        (merged as Record<string, unknown>)[stateKey]
                    )
                    if (serialized !== null) {
                        next.set(urlKey, serialized)
                    } else {
                        next.delete(urlKey)
                    }
                }
                return next
            },
            { replace }
        )
    }

    const isDefault = Object.keys(codec).every((key) => {
        const field = codec[key]
        return field.serialize(state[key]) === null
    })

    return { state, setState, isDefault }
}
